// src/pages/Listing/RelatedCategories.tsx
import React, { useState, useEffect } from 'react';
import { Card, CardBody, CardHeader, ListGroup, ListGroupItem } from 'reactstrap';
import { Link } from 'react-router-dom';
import { Contractor } from '../../models/Contractor.interface';
import { buildCategoryUrl } from '../../utils/urlUtils';

interface RelatedCategoriesProps {
  currentCategory: string;
  contractors: Contractor[];
}

interface CategoryData {
  name: string;
  count: number;
  slug: string;
}

const RelatedCategories: React.FC<RelatedCategoriesProps> = ({ 
  currentCategory, 
  contractors 
}) => {
  const [relatedCategories, setRelatedCategories] = useState<CategoryData[]>([]);

  useEffect(() => {
    // Extract all categories from contractors that have the current category
    const categoryMap = new Map<string, number>();
    const currentNormalized = currentCategory.replace(/-/g, ' ').toLowerCase();
    
    contractors.forEach(contractor => {
      if (contractor.categories) {
        // Filter out the current category
        contractor.categories.forEach(category => {
          const categoryNormalized = category.toLowerCase();
          if (categoryNormalized !== currentNormalized) {
            const count = categoryMap.get(category) || 0;
            categoryMap.set(category, count + 1);
          }
        });
      }
    });

    // Convert to array, sort by count
    const categoriesArray = Array.from(categoryMap.entries())
      .map(([name, count]) => ({
        name,
        count,
        slug: name.toLowerCase().replace(/\s+/g, '-')
      }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 10); // Limit to top 10
    
    setRelatedCategories(categoriesArray);
  }, [currentCategory, contractors]);

  if (relatedCategories.length === 0) return null;

  return (
    <Card className="mb-4">
      <CardHeader>
        <h5 className="card-title mb-0">Related Categories</h5>
      </CardHeader>
      <CardBody>
        <ListGroup flush>
          {relatedCategories.map((category, index) => (
            <ListGroupItem key={index} className="border-0 px-0">
              <Link to={buildCategoryUrl(category.name)} className="d-flex justify-content-between align-items-center">
                <span>{category.name}</span>
                <span className="badge bg-light text-dark rounded-pill">{category.count}</span>
              </Link>
            </ListGroupItem>
          ))}
        </ListGroup>
      </CardBody>
    </Card>
  );
};

export default RelatedCategories;